import { useEffect, useLayoutEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Lightbulb, X } from "lucide-react";
import { useStore } from "../context/StoreContext";
import { TIPS, tipsDue, markTipsSeen, turnOffTips } from "../lib/tour";
import { Button } from "./ui/button";

const el = (t) => {
  const n = document.querySelector(`[data-testid="${t}"]`);
  const r = n?.getBoundingClientRect();
  return r && r.width > 0 && r.height > 0 ? n : null;
};
const busy = () => document.querySelector('[role="dialog"], [data-testid="whats-new-dialog"]');

function useRect(node) {
  const [rect, setRect] = useState(null);
  useLayoutEffect(() => {
    if (!node) return;
    node.scrollIntoView({ block: node.getBoundingClientRect().height > window.innerHeight * 0.5 ? "start" : "center", behavior: "instant" });
    const read = () => setRect(node.getBoundingClientRect());
    read();
    window.addEventListener("resize", read);
    window.addEventListener("scroll", read, true);
    return () => { window.removeEventListener("resize", read); window.removeEventListener("scroll", read, true); };
  }, [node]);
  return rect;
}

function TipCard({ tip, node, i, n, onNext, onSkip }) {
  const r = useRect(node);
  useEffect(() => {
    const k = (e) => e.key === "Escape" && onSkip();
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [onSkip]);
  if (!r) return null;
  const W = Math.min(320, window.innerWidth - 24);
  const H = 200;
  const vh = window.innerHeight;
  // Below the target, else above it, else pinned inside the viewport (tall targets)
  const top = r.bottom + 14 + H < vh ? r.bottom + 14 : r.top - 14 - H > 0 ? r.top - 14 - H : Math.max(12, vh - H - 80);
  const left = Math.max(12, Math.min(r.left, window.innerWidth - W - 12));
  return (
    <div className="fixed inset-0 z-[80]" data-testid="page-tip" onClick={(e) => e.target === e.currentTarget && onSkip()}>
      <div className="absolute rounded-xl ring-2 ring-primary pointer-events-none transition-[top,left,width,height] duration-200" style={{ top: r.top - 6, left: r.left - 6, width: r.width + 12, height: r.height + 12, boxShadow: "0 0 0 9999px rgba(15,23,42,0.45)" }} />
      <div className="absolute rounded-2xl bg-card text-card-foreground border shadow-xl p-4 fade-up" style={{ width: W, left, top }} role="status">
        <div className="flex items-start justify-between gap-2">
          <p className="font-semibold flex items-center gap-2"><Lightbulb className="h-4 w-4 text-primary" /> <span data-testid="page-tip-title">{tip.h}</span></p>
          <button onClick={onSkip} aria-label="Close tips" className="text-muted-foreground hover:text-foreground" data-testid="page-tip-close"><X className="h-4 w-4" /></button>
        </div>
        <p className="text-sm text-muted-foreground mt-1.5">{tip.p}</p>
        <div className="flex items-center justify-between mt-3">
          <button className="text-xs text-muted-foreground underline" onClick={() => { turnOffTips(); onSkip(); }} data-testid="page-tip-off">Don't show tips</button>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground" data-testid="page-tip-step">{i + 1} of {n}</span>
            <Button size="sm" onClick={onNext} data-testid="page-tip-next">{i + 1 === n ? "Got it" : "Next"}</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Shows a screen's tips the first time it's opened on this device
export default function PageTips() {
  const { pathname } = useLocation();
  const { state } = useStore();
  const [tour, setTour] = useState(null);
  useEffect(() => {
    setTour(null);
    if (!state.setupDone || !tipsDue(pathname)) return;
    let tries = 0;
    const t = setInterval(() => {
      tries++;
      if (tries > 30) return clearInterval(t);
      if (busy()) return;
      const steps = TIPS[pathname].filter((s) => el(s.t));
      if (steps.length && tries >= 3) {
        clearInterval(t);
        setTour({ route: pathname, steps, i: 0 });
      }
    }, 400);
    return () => clearInterval(t);
  }, [pathname, state.setupDone]);
  if (!tour) return null;
  const end = () => { markTipsSeen(tour.route); setTour(null); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const step = tour.steps[tour.i];
  const node = el(step.t);
  const next = () => {
    let j = tour.i + 1;
    while (j < tour.steps.length && !el(tour.steps[j].t)) j++;
    if (j >= tour.steps.length) return end();
    setTour({ ...tour, i: j });
  };
  if (!node) return null;
  return <TipCard key={tour.i} tip={step} node={node} i={tour.i} n={tour.steps.length} onNext={next} onSkip={end} />;
}
